import React from 'react';
import { useTranslation } from 'react-i18next';

const ChevronRight: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={`w-4 h-4 flex-shrink-0 ${className}`} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const Step: React.FC<{ children: React.ReactNode; variant: 'primary' | 'accent' }> = ({ children, variant }) => {
  const bg = variant === 'primary'
    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
    : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300';

  return (
    <span className={`inline-block px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${bg}`}>
      {children}
    </span>
  );
};

const CurvedArrow: React.FC = () => (
  <div className="flex justify-center my-2" aria-hidden="true">
    <svg width="120" height="40" viewBox="0 0 120 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-neutral-400 dark:text-neutral-500">
      <path
        d="M10 5 C 10 5, 60 5, 60 20 C 60 35, 110 35, 110 35"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="4 3"
        fill="none"
      />
      <polygon points="110,31 118,35 110,39" fill="currentColor" />
    </svg>
  </div>
);

const LearningPathsDiagram: React.FC = () => {
  const { t } = useTranslation('dashboard');

  const track1Steps = t('learningPaths.track1.steps', { returnObjects: true }) as string[];
  const track2Steps = t('learningPaths.track2.steps', { returnObjects: true }) as string[];

  return (
    <div className="space-y-4">
      {/* Track 1: Teaching */}
      <div>
        <p className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-2">
          {t('learningPaths.track1.label')}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {track1Steps.map((step, i) => (
            <React.Fragment key={i}>
              <Step variant="primary">{step}</Step>
              {i < track1Steps.length - 1 && (
                <ChevronRight className="text-primary-300 dark:text-primary-600" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Curved arrow connecting tracks */}
      <div className="flex items-center gap-2 pl-4">
        <CurvedArrow />
        <span className="text-xs text-neutral-medium dark:text-gray-400 italic">
          {t('learningPaths.connectionLabel')}
        </span>
      </div>

      {/* Track 2: Written Preparation */}
      <div>
        <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-2">
          {t('learningPaths.track2.label')}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {track2Steps.map((step, i) => (
            <React.Fragment key={i}>
              <Step variant="accent">{step}</Step>
              {i < track2Steps.length - 1 && (
                <ChevronRight className="text-amber-300 dark:text-amber-600" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LearningPathsDiagram;
